import { useEffect, useState, useRef, useMemo } from 'react'
import { Tooltip, Popover } from 'antd'
import {
  Check,
  LoaderCircle,
  CirclePlay,
  CirclePause,
  ChevronLeft,
  ChevronRight,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react'
import { isNil, cloneDeep } from 'lodash'
import { useResponsive, useEventListener } from 'ahooks'
import { Button } from '@/components/ui/button'

import styles from './index.module.less'

import { message } from '@/App'
import { evaluate } from '@/api/chat'
import {
  globalActions,
  chatState,
  chatAction,
  globalState,
  configState,
} from '@/store'
import { datefmtMilliSecondFn } from '@/utils/formate'
import reportAction from '@/utils/statistic'
import { RoleTypes, ThumbTypes } from '@/constants/index'
import { copyToClipboard } from '@/utils/copyToClipboard'
import FeedBack from './Feedback'
import { copyIcon, copyMarkdownIcon, refreshIcon } from './SVGIcon'

const ThumbIcon = ({ renderItem }: any) => {
  const [likeStatu, setLikeStatu] = useState(0)
  const responsive = useResponsive()

  useEffect(() => {
    setLikeStatu(renderItem.evalutetype ?? 0)
  }, [renderItem.evalutetype])

  const updateMessage = (likeType: number) => {
    const messages = cloneDeep(chatState.sessionMessages)
    const originAnswerItem = messages.find(
      (i: any) =>
        i.role === RoleTypes.assistant &&
        i.question_id === renderItem.question_id,
    ) as any
    const { answer_list } = originAnswerItem
    const curItem =
      renderItem.answer_id === originAnswerItem.answer_id
        ? originAnswerItem
        : answer_list.find((i: any) => {
            return renderItem.answer_id === i.answer_id
          })
    curItem.evalutetype = likeType
    originAnswerItem.answer_list = answer_list
    chatAction.updateMessages(messages)
  }

  const handleEvalute = async (likeType: number, ratecontent: string = '') => {
    if (likeStatu === likeType) {
      likeType = ThumbTypes.empty
    }
    setLikeStatu(likeType)
    try {
      await evaluate({
        evalutetype: likeType,
        answer_id: renderItem.answer_id,
        session_id: chatState.sessionId,
        ratecontent,
      })
      updateMessage(likeType)
      if (likeType === ThumbTypes.empty) {
        message.success('已取消反馈')
      } else {
        message.success('感谢您的反馈')
      }
      reportAction(
        {
          action: `evalute_${likeType}`,
          user_id: globalState.userInfo.id,
        },
        {
          session_id: chatState.sessionId,
          answer_id: renderItem.answer_id,
          time: datefmtMilliSecondFn(),
        },
      )
    } catch (error) {
      console.log('evaluate error =>', error)
    }
  }

  if (isNil(renderItem.evalutetype)) {
    return null
  }

  return (
    <>
      <Tooltip title="答的不错" open={responsive.sm ? undefined : false}>
        <Button
          size="iconxs"
          variant="custom"
          onClick={() => {
            handleEvalute(ThumbTypes.good)
          }}
        >
          <ThumbsUp
            size={16}
            className={likeStatu === ThumbTypes.good ? 'text-primary' : ''}
          />
        </Button>
      </Tooltip>
      <Tooltip title="还不够好" open={responsive.sm ? undefined : false}>
        {likeStatu === ThumbTypes.bad ? (
          <Button
            size="iconxs"
            variant="custom"
            onClick={() => {
              handleEvalute(ThumbTypes.empty)
            }}
            className={`${styles.unlikeIcon} ${styles.active}`}
          >
            <ThumbsDown size={16} color="#cb3a3a" />
          </Button>
        ) : (
          <Popover
            trigger="click"
            rootClassName={styles.popover}
            destroyTooltipOnHide={true}
            placement="bottom"
            title="您的反馈将帮助紫东太初大模型持续进步"
            content={
              <FeedBack
                submit={(ratecontent: string) => {
                  handleEvalute(ThumbTypes.bad, ratecontent)
                }}
              />
            }
          >
            <Button size="iconxs" variant="custom">
              <ThumbsDown size={16} />
            </Button>
          </Popover>
        )}
      </Tooltip>
    </>
  )
}

const InteractOperate = ({
  isLast,
  renderItem,
  handleNext,
  handlePre,
  curIndex,
}: any) => {
  const [isMdCopied, setIsMdCopied] = useState(false)
  const [isTextCopied, setIsTextCopied] = useState(false)
  const [audioStatu, setAudioStatu] = useState('ready')
  const audioRef = useRef<any>(null)

  const responsive = useResponsive()
  const plainText = useMemo(() => {
    return renderItem.text.replace(/[#*_`\-[\]()>]/g, '')
  }, [renderItem.text])

  useEventListener(
    'ended',
    () => {
      setAudioStatu('ready')
    },
    { target: audioRef },
  )

  useEventListener(
    'pause',
    () => {
      setAudioStatu('ready')
    },
    { target: audioRef },
  )
  useEffect(() => {
    console.log('audioRef.current :>> ', audioRef.current)
    return () => {
      console.log('audioRef.current :>> ssss', audioRef.current)
      audioRef.current?.pause()
    }
  }, [])

  const handleAudioPlay = async () => {
    try {
      setAudioStatu('ready')

      const src = `${
        import.meta.env.VITE_APP_COMMON_BASE_URL
      }/zdtc-api/audioforward?speaker=${
        configState.timbreConfig.speaker
      }&sampleRate=16000&text=${encodeURIComponent(
        `<speak rate='${configState.timbreConfig.rate}'>${plainText}</speak>`,
      )}`
      audioRef.current = new Audio(src)
      globalActions.pauseCurrentAudio(audioRef.current)
      // 重新生成多条答案独立状态
      setAudioStatu(`pending${renderItem.answer_id}`)
      await audioRef.current.play()
      setAudioStatu(`playing${renderItem.answer_id}`)
    } catch (error) {
      console.log('error: ', error)
      setAudioStatu('ready')
    }
  }

  const handleAudioPause = async () => {
    audioRef.current?.pause()
  }

  const handleCopy = async () => {
    if (isTextCopied) return
    await copyToClipboard(plainText)
    setIsTextCopied(true)
    message.success('复制成功')
    setTimeout(() => {
      setIsTextCopied(false)
    }, 2000)
  }

  const handleCopyMD = async () => {
    if (isMdCopied) return
    await copyToClipboard(renderItem.text)
    setIsMdCopied(true)
    message.success('复制成功')
    setTimeout(() => {
      setIsMdCopied(false)
    }, 2000)
  }

  return (
    <div className={styles.interactOperate}>
      <div className={styles.toolbar}>
        {renderItem.text && (
          <>
            <Tooltip title="复制内容" open={responsive.sm ? undefined : false}>
              <Button size="iconxs" variant="custom" onClick={handleCopy}>
                {isTextCopied ? <Check size={16} /> : copyIcon}
              </Button>
            </Tooltip>
            <Tooltip
              title="复制成 markdown"
              open={responsive.sm ? undefined : false}
            >
              <Button size="iconxs" variant="custom" onClick={handleCopyMD}>
                {isMdCopied ? <Check size={16} /> : copyMarkdownIcon}
              </Button>
            </Tooltip>

            {responsive.md &&
              renderItem.text.length < 1000 &&
              !/```/.test(renderItem.text) && (
                <Tooltip
                  title="语音播放"
                  open={responsive.sm ? undefined : false}
                >
                  <Button size="iconxs" variant="custom">
                    {audioStatu === `pending${renderItem.answer_id}` ? (
                      <LoaderCircle size={16} className="animate-spin" />
                    ) : audioStatu === `playing${renderItem.answer_id}` ? (
                      <CirclePause size={16} onClick={handleAudioPause} />
                    ) : (
                      <CirclePlay size={16} onClick={handleAudioPlay} />
                    )}
                  </Button>
                </Tooltip>
              )}
          </>
        )}
        {isLast && (
          <Tooltip
            title={
              renderItem.answer_list?.length === 4
                ? '当前最多支持5次重新生成哦~'
                : '重新生成'
            }
          >
            <Button
              size="iconxs"
              variant="custom"
              disabled={renderItem.answer_list?.length === 4}
              onClick={() => {
                chatAction.reAnswer(renderItem.question_id)
              }}
            >
              {refreshIcon}
            </Button>
          </Tooltip>
        )}
        {renderItem.answer_list?.length > 0 &&
          renderItem.out_finished !== false && (
            <div className="flex items-center text-xs">
              <Button
                size="iconxs"
                variant="custom"
                disabled={renderItem.loading}
                onClick={handlePre}
              >
                <ChevronLeft size={16} />
              </Button>
              <span>
                {curIndex + 1} / {(renderItem.answer_list.length as number) + 1}
              </span>
              <Button
                size="iconxs"
                variant="custom"
                disabled={renderItem.loading}
                onClick={handleNext}
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          )}
        {renderItem.text && <div className="bg-[#1C1F231A] w-[1px] h-[12px]" />}
        <ThumbIcon renderItem={renderItem} />
      </div>
    </div>
  )
}

export default InteractOperate
